/* The /registry-viewer page.
 *
 * No worker, and that is measured rather than assumed: the 45 MB SOFTWARE hive
 * parses in 487 ms and carving its free space adds little. What a worker would
 * add is a copy of the file and a message protocol; what it would save is
 * nothing anyone would notice.
 *
 * The tree is built lazily. A hive holds 150 000 keys and 260 000 values, and
 * materialising that as DOM is how a page dies — children are created when a
 * node is opened, values only when a key is selected.
 */

import { inspect, walk, valuesOf, carveDeleted } from './hive-read.js'
import { reportShape } from '../file-shape.js'
import { suggestOtherTool } from '../file-signature.js'

const T = window.__HIVE_I18N || {}
const t = (k, fallback, vars) => {
  let s = T[k] || fallback || k
  /* Longest placeholder first: ':n' is a prefix of ':name'. */
  if (vars) {
    for (const [n, v] of Object.entries(vars).sort((a, b) => b[0].length - a[0].length)) {
      s = s.split(':' + n).join(String(v))
    }
  }
  return s
}
const $ = s => document.querySelector(s)
const el = (tag, cls, text) => {
  const n = document.createElement(tag)
  if (cls) n.className = cls
  if (text != null) n.textContent = text
  return n
}

const state = { info: null, tree: null, name: '', selected: null, limit: null, deleted: null }

function setStatus(msg, kind = 'info') {
  const box = $('#hv-status')
  if (!box) return
  box.hidden = !msg
  box.className = 'hv-status lg-card hv-status--' + kind
  box.textContent = msg || ''
}

const fmtBytes = n => {
  const u = [t('unit_b', 'B'), t('unit_kb', 'KB'), t('unit_mb', 'MB')]
  let i = 0
  let v = n
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++ }
  return `${v < 10 && i ? v.toFixed(1) : Math.round(v)} ${u[i]}`
}

const fmtDate = d => (d ? d.toISOString().slice(0, 19).replace('T', ' ') : '—');

async function open(file) {
  state.name = file.name
  setStatus(t('opening', 'Reading the hive…'))
  $('#hv-workspace').hidden = true
  $('#hv-summary').hidden = true

  const bytes = new Uint8Array(await file.arrayBuffer())
  let info
  try {
    info = inspect(bytes)
  } catch (e) {
    /* Unrecognised. This tool was wired before the rule below was settled and
       so stayed silent here — losing the most valuable report of all: a file
       whose signature we do not know is exactly what a parser has to learn. */
    try {
      reportShape({ format: 'regf', size: bytes.length, ok: false, reason: 'not-a-hive' })
    } catch (_) {}
    setStatus(t('err_not_hive', 'This file is not a Windows registry hive.'), 'error')
    try { window.__stage?.('error') } catch (_) {}
    suggestOtherTool(file, 'registry-viewer', document.querySelector('#hv-status'), { text: t('suggest_text'), link: t('suggest_link') })
    state.limit?.abort()
    return
  }
  state.info = info

  const t0 = performance.now()
  state.tree = walk(info.hive)
  state.deleted = carveDeleted(info.hive, { limit: 5000 })
  const ms = Math.round(performance.now() - t0)

  /* Report the SHAPE of anything we could not fully read — including the
     partial successes, which are the cases nobody writes in about because the
     page showed them something. No bytes leave; see file-shape.js. */
  /* Reported on EVERY open, not only on damage. Which versions and sizes
     actually arrive is half the question a parser fix needs answered, and the
     healthy files are the baseline the damaged ones are read against. Same
     rule in all six tools, so there is one thing to verify rather than six. */
  {
    reportShape({
      format: 'regf',
      version: `${info.header.major}.${info.header.minor}`,
      size: bytes.length,
      ok: !!state.tree.root,
      reason: info.problems[0] || null,
      flags: info.problems,
      counts: { keys: state.tree.keys, values: state.tree.values, bins: info.bins, free: info.freeCells },
    })
  }

  if (!state.tree.root) {
    setStatus(t('err_no_root', 'No keys could be read out of this hive.'), 'error')
    try { window.__stage?.('error') } catch (_) {}
    suggestOtherTool(file, 'registry-viewer', document.querySelector('#hv-status'), { text: t('suggest_text'), link: t('suggest_link') })
    state.limit?.abort()
    return
  }

  setStatus('')
  renderSummary(ms)
  renderTree()
  renderDeleted()
  try { window.__stage?.('complete') } catch (_) {}
  $('#hv-workspace').hidden = false
  state.limit?.commit()
  window.__aeToolComplete?.({ keys: state.tree.keys, deleted: state.deleted.keys.length })
}

function renderSummary(ms) {
  const box = $('#hv-summary')
  const i = state.info
  const h = i.header
  box.innerHTML = ''
  box.hidden = false

  const chips = el('div', 'hv-chips')
  const chip = (label, value, kind = '') => {
    if (value == null || value === '') return
    const c = el('span', 'hv-chip' + (kind ? ' hv-chip--' + kind : ''))
    c.append(el('b', null, label), document.createTextNode(' '), el('span', null, String(value)))
    chips.append(c)
  }
  chip(t('chip_size', 'Size'), fmtBytes(i.hive.b.length))
  chip(t('chip_keys', 'Keys'), state.tree.keys.toLocaleString())
  chip(t('chip_values', 'Values'), state.tree.values.toLocaleString())
  chip(t('chip_written', 'Last written'), fmtDate(h.timestamp))
  chip(t('chip_version', 'Version'), `${h.major}.${h.minor}`)
  chip(t('chip_free', 'Free space'), fmtBytes(i.freeBytes))
  if (state.deleted.keys.length) {
    chip(t('chip_deleted', 'Deleted keys'), state.deleted.keys.length.toLocaleString(), 'accent')
  }
  box.append(chips)

  const notes = el('ul', 'hv-notes')
  const note = (text, key = false) => notes.append(el('li', key ? 'hv-note--key' : null, text))

  if (h.dirty) {
    note(t('note_dirty',
      'The two sequence numbers in the header differ: this hive was copied while Windows was writing to it. The rest of that transaction is in the .LOG files next to it. Everything below is still readable — the file is simply a moment older than the logs.'), true)
  }
  if (!h.checksumOk) {
    note(t('note_checksum', 'The header checksum does not match. Windows would refuse to load this file; the keys below were read anyway.'), true)
  }
  if (i.problems.includes('bin-header')) {
    note(t('note_bin', 'One or more block headers are damaged. The reader stepped over them and continued — nothing after the damage was lost.'), true)
  }
  if (i.problems.includes('root')) {
    note(t('note_root', 'The header points at a root key that is not there. The real root was found by scanning, which is why this hive opens here and not elsewhere.'), true)
  }
  if (i.problems.includes('truncated')) {
    note(t('note_truncated', 'The file is shorter than its header claims: it was cut off. Only the surviving part was read.'), true)
  }
  note(t('note_read', 'Read in :ms ms — :bins blocks, :used cells in use, :free free.',
    { ms, bins: i.bins.toLocaleString(), used: i.usedCells.toLocaleString(), free: i.freeCells.toLocaleString() }))

  box.append(el('h4', 'hv-sub', t('notes_title', 'What the file says about itself')), notes)
}

/* ---------------------------- the key tree ---------------------------- */

function nodeRow(node, path) {
  const row = el('div', 'hv-node')
  row.style.paddingInlineStart = (node.depth * 14) + 'px'
  const twisty = el('button', 'hv-twisty', node.children.length ? '▸' : '·')
  twisty.type = 'button'
  if (!node.children.length) twisty.disabled = true
  const name = el('button', 'hv-key', node.name || '(default)')
  name.type = 'button'
  if (node.valueCount) name.append(el('span', 'hv-count', String(node.valueCount)))
  row.append(twisty, name)

  const kids = el('div', 'hv-kids')
  kids.hidden = true
  let built = false

  twisty.addEventListener('click', () => {
    kids.hidden = !kids.hidden
    twisty.textContent = kids.hidden ? '▸' : '▾'
    if (!built) {
      built = true
      /* Children are created on first open. Building 150 000 rows up front is
         how a hive viewer becomes a frozen tab. */
      for (const c of node.children) kids.append(...nodeRow(c, path + '\\' + c.name))
    }
  })
  name.addEventListener('click', () => {
    for (const b of document.querySelectorAll('.hv-key.is-on')) b.classList.remove('is-on')
    name.classList.add('is-on')
    state.selected = { node, path }
    renderValues()
  })
  return [row, kids]
}

function renderTree() {
  const host = $('#hv-tree')
  host.innerHTML = ''
  const r = state.tree.root
  host.append(...nodeRow(r, r.name))
  if (state.tree.truncated) {
    host.append(el('p', 'hv-muted', t('tree_truncated', 'The tree was cut off at :n keys.', { n: state.tree.keys })))
  }
}

function renderValues() {
  const host = $('#hv-values')
  host.innerHTML = ''
  if (!state.selected) return
  const { node, path } = state.selected
  host.append(el('p', 'hv-path', path))
  const vals = valuesOf(state.info.hive, node.at)
  if (!vals.length) {
    host.append(el('p', 'hv-muted', t('no_values', 'This key holds no values.')))
    return
  }
  host.append(valueTable(vals))
}

function valueTable(vals) {
  const table = el('table', 'hv-grid')
  const thead = el('thead')
  const hr = el('tr')
  for (const h of [t('col_name', 'Name'), t('col_type', 'Type'), t('col_value', 'Value')]) hr.append(el('th', null, h))
  thead.append(hr)
  const tbody = el('tbody')
  for (const v of vals) {
    const tr = el('tr')
    tr.append(el('td', null, v.name || '(default)'))
    tr.append(el('td', 'hv-type', v.type))
    tr.append(el('td', null, renderValueText(v)))
    tbody.append(tr)
  }
  table.append(thead, tbody)
  return table
}

function renderValueText(v) {
  const val = v.value
  if (val == null) return '—'
  if (val instanceof Uint8Array) {
    const hex = [...val.slice(0, 24)].map(b => b.toString(16).padStart(2, '0')).join(' ')
    return val.length > 24 ? `${hex} … (${fmtBytes(val.length)})` : hex || '—'
  }
  if (Array.isArray(val)) return val.join(' · ')
  return String(val)
}

/* ------------------------- deleted, but on disk ------------------------- */

function renderDeleted() {
  const host = $('#hv-deleted')
  host.innerHTML = ''
  const d = state.deleted
  if (!d.keys.length && !d.values.length) {
    host.append(el('p', 'hv-muted', t('no_deleted', 'No deleted records survive in this hive\'s free space.')))
    return
  }
  host.append(el('p', 'hv-muted', t('deleted_intro',
    'Deleting a registry key does not erase it: Windows marks the space free and leaves the record where it is. These were found in that free space, and any of them may have been overwritten in part.')))

  if (d.keys.length) {
    const table = el('table', 'hv-grid')
    const thead = el('thead')
    const hr = el('tr')
    for (const h of [t('col_name', 'Name'), t('col_deleted_when', 'Last written'), t('col_values', 'Values')]) hr.append(el('th', null, h))
    thead.append(hr)
    const tbody = el('tbody')
    for (const k of d.keys.slice(0, 500)) {
      const tr = el('tr')
      tr.append(el('td', null, k.name))
      tr.append(el('td', 'hv-type', fmtDate(k.timestamp)))
      tr.append(el('td', 'hv-type', String(k.valueCount)))
      tbody.append(tr)
    }
    table.append(thead, tbody)
    host.append(el('h4', 'hv-sub', t('deleted_keys_title', 'Deleted keys (:n)', { n: d.keys.length })), table)
  }
  if (d.values.length) {
    host.append(el('h4', 'hv-sub', t('deleted_values_title', 'Deleted values (:n)', { n: d.values.length })),
      valueTable(d.values.slice(0, 500)))
  }
}

/* ------------------------------- export ------------------------------- */

const stem = () => state.name.replace(/\.[^.]+$/, '') || 'hive'

/** A .reg file Windows itself can import — the format people actually want. */
function exportReg() {
  if (!state.tree?.root) return
  const lines = ['Windows Registry Editor Version 5.00', '']
  const esc = s => s.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
  const emit = (node, path) => {
    const vals = valuesOf(state.info.hive, node.at)
    if (vals.length) {
      lines.push(`[${path}]`)
      for (const v of vals) {
        const name = v.name ? `"${esc(v.name)}"` : '@'
        if (v.type === 'REG_SZ' || v.type === 'REG_EXPAND_SZ') {
          lines.push(`${name}="${esc(String(v.value ?? ''))}"`)
        } else if (v.type === 'REG_DWORD') {
          lines.push(`${name}=dword:${(v.value >>> 0).toString(16).padStart(8, '0')}`)
        } else if (v.value instanceof Uint8Array) {
          lines.push(`${name}=hex:${[...v.value].map(b => b.toString(16).padStart(2, '0')).join(',')}`)
        } else {
          lines.push(`${name}="${esc(String(v.value ?? ''))}"`)
        }
      }
      lines.push('')
    }
    for (const c of node.children) emit(c, path + '\\' + c.name)
  }
  emit(state.tree.root, state.tree.root.name)
  /* Windows expects UTF-16LE with a BOM; a UTF-8 .reg imports as mojibake. */
  const text = lines.join('\r\n')
  const buf = new Uint8Array(2 + text.length * 2)
  buf[0] = 0xff; buf[1] = 0xfe
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i)
    buf[2 + i * 2] = c & 0xff
    buf[3 + i * 2] = c >> 8
  }
  /* Our own export of the person's own hive — the one program-type file a tool
     here produces on purpose, so it is declared (see __isProgramFile). */
  window.__downloadFile?.(new Blob([buf], { type: 'application/octet-stream' }), stem() + '.reg', { program: true })
}

function exportDeletedCsv() {
  const d = state.deleted
  if (!d?.keys.length && !d?.values.length) return
  const q = s => '"' + String(s ?? '').replace(/"/g, '""') + '"'
  const rows = [['kind', 'name', 'type', 'last_written', 'value'].map(q).join(',')]
  for (const k of d.keys) rows.push(['key', k.name, '', fmtDate(k.timestamp), ''].map(q).join(','))
  for (const v of d.values) rows.push(['value', v.name, v.type, '', renderValueText(v)].map(q).join(','))
  window.__downloadFile?.(new Blob(['\uFEFF' + rows.join('\r\n')], { type: 'text/csv' }), stem() + '-deleted.csv')
}

function wire() {
  const drop = document.getElementById('hiveDrop')
  drop?.addEventListener('tool-uploader:file', async ev => {
    const file = ev.detail?.files?.length ? ev.detail.files[0] : ev.detail?.file
    if (!file) return
    /* Funnel stages. Without them a tool can only report that it finished —
       never that someone arrived, picked a file and got nothing, which is the
       case worth knowing about because that person leaves without writing in. */
    try { window.__stage?.('file_chosen', { mb: Math.round((file.size || 0) / 1048576) }) } catch (_) {}
    state.limit = window.__toolLimit?.begin('registry-viewer', file.name + ':' + file.size) || null
    try {
      await open(file)
    } catch (err) {
      try { window.__stage?.('error') } catch (_) {}
    suggestOtherTool(file, 'registry-viewer', document.querySelector('#hv-status'), { text: t('suggest_text'), link: t('suggest_link') })
    state.limit?.abort()
      setStatus(t('err_open', 'This file could not be read: ') + (err?.message || err), 'error')
      window.__handleError?.(err, 'registry-viewer')
    }
  })
  drop?.addEventListener('tool-uploader:clear', () => {
    state.info = null; state.tree = null; state.deleted = null
    $('#hv-workspace').hidden = true
    $('#hv-summary').hidden = true
    setStatus('')
  })
  $('#hv-export-reg')?.addEventListener('click', exportReg)
  $('#hv-export-deleted')?.addEventListener('click', exportDeletedCsv)
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wire)
else wire()
